import { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { TableCell, TableRow } from '@mui/material'
import Content from 'components/content/content'
import CONSTANT from 'modules/constant'
import GLOBAL from 'modules/global'
import type { ErrorProps } from 'modules/types'
import Notification from 'components/notification'
import { useGetReferralOwnerListMutation, useDownloadReferralOwnerListMutation, type ReferralOwner } from 'store/referral'

interface PartialReferralOwner {
  start: number
  length: number
  search: string
}

const ReferralOwnerList = () => {
  const [getListSubmissions, submissionsList] = useGetReferralOwnerListMutation()
  const [downloadReferralOwnerList, download] = useDownloadReferralOwnerListMutation()

  const [payload, setPayload] = useState<PartialReferralOwner>({
    start: 0,
    length: CONSTANT.DEFAULT_PAGINATION_PER_PAGE,
    search: ''
  })


  const onSearch = (value: string) => setPayload({ ...payload, start: 0, search: value })
  const onChangePage = (value: number) => setPayload({ ...payload, start: value - 1 })
  const onChangePerPage = (value: number) => setPayload({ ...payload, length: value })

  const getField = () =>
    columns
      .filter((c) => c.searchable !== false)
      .map((c) => c.key)
      .join(',')

  const onResync = () => {
    const formData = new FormData()

    formData.append('start', payload.start.toString())
    formData.append('length', payload.length.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)

    getListSubmissions(formData)
  }

  const onDownload = (type?: string) => {
    const formData = new FormData()

    formData.append('start', String(0))
    formData.append('length', CONSTANT.DEFAULT_PAGINATION_MAXIMUM_DOWNLOAD.toString())
    formData.append('field', getField())

    if (!isEmpty(payload.search)) formData.append('search[value]', payload.search)
    if (type && !isEmpty(type)) formData.append('Download-Type', type)

    downloadReferralOwnerList(formData)
  }

  const columns = [
    {
      column: 'Referrer Member ID',
      label: 'Referrer Member ID',
      searchable: true,
      key: 'referrerMemberId'
    },
    {
      column: 'Referrer Name',
      label: 'Referrer Name',
      searchable: true,
      key: 'referrerName'
    },
    {
      column: 'Referral VIN',
      label: 'Referral VIN',
      searchable: true,
      key: 'referralVin'
    },
    {
      column: 'VIN Status',
      label: 'VIN Status',
      searchable: true,
      key: 'referralVinStatus'
    },
    {
      column: 'Referral Name',
      label: 'Referral Name',
      searchable: true,
      key: 'referralName'
    },
    {
      column: 'Registration Submission Date',
      label: 'Registration Submission Date',
      searchable: false,
      key: 'registrationSubmissionDate',
      render: (row: ReferralOwner) => GLOBAL.formatDateWithTime(row.registrationSubmissionDate)
    },

  ]

  const tableHeadTitles = columns.map((c) => c.label)

  const totalPage = submissionsList.data && GLOBAL.tableTotalPage(submissionsList.data.recordsTotal)
  const numbers = GLOBAL.tableNumber(payload.start)

  useEffect(() => {
    onResync()
  }, [payload])

  return (
    <>
      <Content
        title='Referral Owner Submissions'
        tableHeadTitles={tableHeadTitles}
        onSearch={onSearch}
        onResync={onResync}
        onDownload={onDownload}
        downloadLoading={download.isLoading}
        onDownloadTypeIgnored={['pdf']}
        pagination={onChangePage}
        onChangePerPage={onChangePerPage}
        totalPage={totalPage}
        totalRecords={submissionsList.data && submissionsList.data.recordsTotal}
        page={payload.start + 1}
        isLoading={submissionsList.isLoading}
        useAction={false}
        useDownloadDropdown
      >
        {submissionsList.isSuccess &&
          submissionsList.data &&
          submissionsList.data.data?.map((table, index) => (
            <TableRow key={index}>
              <TableCell>{numbers[index] + 1}</TableCell>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  {col.render
                    ? col.render(table as ReferralOwner)
                    : table[col.key as keyof ReferralOwner] || '-'}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </Content>

      <Notification
        open={submissionsList.isError}
        onClose={() => {
          submissionsList.reset()
        }}
        isError={submissionsList.isError}
        message={GLOBAL.returnExceptionMessage(
          submissionsList.isError,
          submissionsList.error as ErrorProps
        )}
      />
    </>
  )
}

export default ReferralOwnerList